// Crew register: calm summary rows open a single edit modal, keeping contact
// details and notes available without making the directory scan like a form.

import { useViewState } from '../lib/useViewState';
import { str } from '../lib/viewState';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import type { CrewContactWithBookings, ProjectPhase } from '../../shared/types';
import {
  formatMoney,
  formatProjectNumber,
  Modal,
  PageHeader,
  parseMoney,
  Pill,
  SearchBox,
  SkeletonRows,
  TrashAction,
} from '../components/primitives';
import { DotsIcon, SearchIcon, TrashIcon } from '../components/icons';
import { PageMenu } from '../components/PageMenu';
import { SheetRow } from '../components/Sheet';
import { crewBookingMeta, crewContext, crewRateColumn } from '../lib/crewMeta';
import { useQuickAddAction } from '../lib/quickAdd';
import { useIsMobile } from '../lib/useIsMobile';

interface CrewBooking {
  project_id: string;
  project_number: number | null;
  project_name: string;
  phase: ProjectPhase;
  start_date: string;
  end_date: string;
  half_day_dates: string | null;
}

interface CrewForm {
  name: string;
  role: string;
  company: string;
  phone: string;
  email: string;
  day_rate: string;
  half_day_rate: string;
  notes: string;
}

type CrewRateField = 'day_rate' | 'half_day_rate';
type CrewRateErrors = Partial<Record<CrewRateField, string>>;

function parseCrewRates(form: Pick<CrewForm, CrewRateField>): {
  dayRate: number | null;
  halfDayRate: number | null;
  errors: CrewRateErrors;
} {
  const dayRate = parseMoney(form.day_rate);
  const halfDayRate = parseMoney(form.half_day_rate);
  const errors: CrewRateErrors = {};

  if (form.day_rate.trim() && dayRate === null) errors.day_rate = 'Enter a valid day rate.';
  if (form.half_day_rate.trim() && halfDayRate === null) {
    errors.half_day_rate = 'Enter a valid half-day rate.';
  }

  return { dayRate, halfDayRate, errors };
}

const PHASE_DOTS: Record<ProjectPhase, string> = {
  lead: 'pd-lead',
  'pre-production': 'pd-pre',
  shoot: 'pd-shoot',
  post: 'pd-post',
  delivered: 'pd-post',
  clean_up: 'pd-cleanup',
  closed: 'pd-hold',
  on_hold: 'pd-hold',
};

function rateDraft(cents: number | null): string {
  return cents === null ? '' : String(cents / 100);
}

function crewForm(crew?: CrewContactWithBookings): CrewForm {
  return {
    name: crew?.name ?? '',
    role: crew?.role ?? '',
    company: crew?.company ?? '',
    phone: crew?.phone ?? '',
    email: crew?.email ?? '',
    day_rate: rateDraft(crew?.day_rate_cents ?? null),
    half_day_rate: rateDraft(crew?.half_day_rate_cents ?? null),
    notes: crew?.notes ?? '',
  };
}

function CrewRow({ crew, onEdit }: { crew: CrewContactWithBookings; onEdit: () => void }) {
  const context = crewContext(crew) ?? '';
  const contactTitle = [crew.email, crew.phone].filter(Boolean).join(' · ') || undefined;
  const dayRate = crew.day_rate_cents === null ? '—' : formatMoney(crew.day_rate_cents);
  const halfRate = crew.half_day_rate_cents === null ? '—' : formatMoney(crew.half_day_rate_cents);
  return (
    <div className="row crew-summary-row" title={contactTitle} onClick={onEdit}>
      <span className="row-title">{crew.name}</span>
      {context && <span className="row-context">{context}</span>}
      <span className="row-spacer" />
      {crew.current_bookings > 0 && (
        <span className="crew-booked">{crew.current_bookings} booked</span>
      )}
      <span className="row-meta gold crew-summary-rate">
        {dayRate} / {halfRate} half
      </span>
    </div>
  );
}

/** Mockup §9.1 A: mark · name over role · company, then the rate stack and
    the booked pill. The column is `.crew-col`, never the mockup's `.main` —
    that is also the app shell's panel class. */
function CrewMobileRow({ crew, onEdit }: { crew: CrewContactWithBookings; onEdit: () => void }) {
  const context = crewContext(crew);
  const rate = crewRateColumn(crew);
  return (
    <button type="button" className="crew-row" onClick={onEdit}>
      <span className="client-mark">{crew.name.slice(0, 1).toUpperCase()}</span>
      <span className="crew-col">
        <span className="crew-row-title">{crew.name}</span>
        {context && <span className="crew-row-meta">{context}</span>}
      </span>
      <span className="crew-right">
        {rate.day === null ? (
          <span className="crew-rate-none">—</span>
        ) : (
          <>
            <span className="crew-rate">{rate.day}</span>
            <span>{rate.half}</span>
          </>
        )}
        {crew.current_bookings > 0 && <Pill tone="accent">{crew.current_bookings} booked</Pill>}
      </span>
    </button>
  );
}

export const crewViewCodec = {
  query: str('q'),
};

export default function Crew() {
  const { initial, publish } = useViewState(crewViewCodec, state => {
    setQuery(state.query);
    setSearchOpen(state.query.trim() !== '');
  });
  const isMobile = useIsMobile();
  const [crew, setCrew] = useState<CrewContactWithBookings[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState(() => initial.query);
  const [showNew, setShowNew] = useState(false);
  const [editingCrew, setEditingCrew] = useState<CrewContactWithBookings | null>(null);
  // Crew has its own search box rather than the command palette, so the
  // header magnifier reveals it as a chip row instead of dispatching
  // `ss:open-search` (Phase C §9.1).
  const [searchOpen, setSearchOpen] = useState(() => initial.query.trim() !== '');

  useEffect(() => {
    publish({ query });
  }, [publish, query]);
  const [menuOpen, setMenuOpen] = useState(false);

  useQuickAddAction({ label: 'New crew', onSelect: () => setShowNew(true) });

  const listSeq = useRef(0);
  const fetchCrew = useCallback(() => {
    const seq = ++listSeq.current;
    api<{ crew: CrewContactWithBookings[] }>('/api/admin/crew')
      .then((d) => {
        if (seq === listSeq.current) setCrew(d.crew);
      })
      .catch(() => {})
      .finally(() => {
        if (seq === listSeq.current) setLoaded(true);
      });
  }, []);
  // Coalesced refetch so a quick-added contact appears without navigating;
  // the page's own mutations call refresh(), which cancels the pending timer
  // so one change never costs two rounds.
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    fetchCrew();
  }, [fetchCrew]);
  useEffect(() => {
    fetchCrew();
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(fetchCrew, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      listSeq.current++;
    };
  }, [fetchCrew]);

  const filtered = useMemo(() => {
    if (!query.trim()) return crew;
    const q = query.trim().toLowerCase();
    return crew.filter((c) =>
      [c.name, c.role, c.company, c.email, c.phone].filter(Boolean).some((s) => (s as string).toLowerCase().includes(q)),
    );
  }, [crew, query]);

  // ── ≤880px (Phase C §9.1 A) ───────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <PageHeader
          title="Crew"
          count={crew.length}
          mobileActions={[
            {
              key: 'search',
              label: 'Search crew',
              icon: <SearchIcon />,
              active: searchOpen || query.trim() !== '',
              onPress: () => {
                setSearchOpen((open) => {
                  if (open) setQuery('');
                  return !open;
                });
              },
            },
            { key: 'menu', label: 'More crew actions', icon: <DotsIcon />, onPress: () => setMenuOpen(true) },
          ]}
        />

        {searchOpen && (
          <div className="chiprow crew-search">
            <SearchBox placeholder="Search name, role, company…" value={query} onChange={setQuery} />
          </div>
        )}

        <div className="page-body">
          <div className="content wide crew-list-m">
            <div className="crew-group">
              <span>Crew &amp; vendors</span>
              <span className="n">{filtered.length}</span>
            </div>
            {!loaded ? (
              <SkeletonRows />
            ) : (
              <>
                {filtered.map((c) => (
                  <CrewMobileRow key={c.id} crew={c} onEdit={() => setEditingCrew(c)} />
                ))}
                {filtered.length === 0 && <div className="empty-note">No crew match.</div>}
              </>
            )}
            <QuickAddCrewRow onAdded={refresh} compact />
          </div>
        </div>

        <PageMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          title="Crew"
          items={[{ key: 'new', label: 'New crew', onSelect: () => setShowNew(true) }]}
        />
        {showNew && <NewCrewModal onClose={() => setShowNew(false)} onCreated={refresh} />}
        {editingCrew && (
          <EditCrewModal
            key={editingCrew.id}
            crew={editingCrew}
            onClose={() => setEditingCrew(null)}
            onChanged={refresh}
          />
        )}
      </>
    );
  }

  return (
    <>
      <PageHeader title="Crew" count={crew.length} />

      <div className="toolbar">
        <SearchBox placeholder="Search name, role, company…" value={query} onChange={setQuery} />
        <button className="btn primary" onClick={() => setShowNew(true)}>
          + New crew
        </button>
      </div>

      <div className="page-body">
        <div className="content wide" style={{ paddingTop: 4 }}>
          <div className="group">
            <div className="group-head">
              <span className="group-title">Crew &amp; vendors</span>
              <span className="group-count">{filtered.length}</span>
            </div>
            {!loaded ? (
              <SkeletonRows />
            ) : (
              <>
                {filtered.map((c) => (
                  <CrewRow key={c.id} crew={c} onEdit={() => setEditingCrew(c)} />
                ))}
                {filtered.length === 0 && <div className="empty-note">No crew match.</div>}
              </>
            )}
            <QuickAddCrewRow onAdded={refresh} />
          </div>
        </div>
      </div>

      {showNew && <NewCrewModal onClose={() => setShowNew(false)} onCreated={refresh} />}
      {editingCrew && (
        <EditCrewModal
          key={editingCrew.id}
          crew={editingCrew}
          onClose={() => setEditingCrew(null)}
          onChanged={refresh}
        />
      )}
    </>
  );
}

// ── Quick-add row — name only, stays open for rapid entry (Tasks' pattern) ─

function QuickAddCrewRow({ onAdded, compact = false }: { onAdded: () => void; compact?: boolean }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');

  const submit = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      await api('/api/admin/crew', { method: 'POST', json: { name: n } });
    } catch (error) {
      reportError(error, 'Could not create the crew contact.');
      return;
    }
    setName('');
    onAdded();
  };

  return (
    <div className="quick-add" onClick={() => setAdding(true)}>
      <span className="plus">+</span>
      {adding ? (
        <input
          value={name}
          placeholder="Add crew contact…"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') {
              setAdding(false);
              setName('');
            }
          }}
          onBlur={() => {
            if (!name.trim()) setAdding(false);
          }}
          autoFocus
        />
      ) : (
        <span className="hint">
          {compact ? 'Add crew contact — name only, edit the rest later' : 'Add crew contact — name only, click the row to edit the rest'}
        </span>
      )}
    </div>
  );
}

// ── Shared create/edit form ──────────────────────────────────────────────

function CrewFormFields({
  form,
  setForm,
  autoFocus = false,
  bookings,
  bookingsLoaded = true,
  error,
  rateErrors,
  onRateEdit,
}: {
  form: CrewForm;
  setForm: React.Dispatch<React.SetStateAction<CrewForm>>;
  autoFocus?: boolean;
  bookings?: CrewBooking[];
  bookingsLoaded?: boolean;
  error: string | null;
  rateErrors?: CrewRateErrors;
  onRateEdit?: (field: CrewRateField) => void;
}) {
  const isMobile = useIsMobile();
  // No shared paired-field class exists — each form names its own (Phase B).
  const pair = isMobile ? 'f-field crew-pair' : 'f-field';
  const set = (key: keyof CrewForm) => (event: { target: { value: string } }) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
    if ((key === 'day_rate' || key === 'half_day_rate') && onRateEdit) onRateEdit(key);
  };

  return (
    <div className={isMobile ? 'form-grid crew-form' : 'form-grid'}>
      <div className="f-field span-2">
        <span className="f-label">
          Name <span className="req">*</span>
        </span>
        <input className="f-input" value={form.name} onChange={set('name')} autoFocus={autoFocus} />
      </div>
      <div className={pair}>
        <span className="f-label">Role</span>
        <input className="f-input" value={form.role} onChange={set('role')} placeholder="DOP, drone op, editor…" />
      </div>
      <div className={pair}>
        <span className="f-label">Company</span>
        <input className="f-input" value={form.company} onChange={set('company')} placeholder="e.g. Refresh Films" />
      </div>
      <div className={pair}>
        <span className="f-label">Phone</span>
        <input className="f-input mono" type={isMobile ? 'tel' : undefined} value={form.phone} onChange={set('phone')} placeholder="04…" />
      </div>
      <div className={pair}>
        <span className="f-label">Email</span>
        <input className="f-input mono" type={isMobile ? 'email' : undefined} value={form.email} onChange={set('email')} placeholder="name@company.com.au" />
      </div>
      <div className={pair}>
        <span className="f-label">{isMobile ? 'Day rate · ex GST' : 'Day rate (ex GST)'}</span>
        <input className="f-input money" value={form.day_rate} onChange={set('day_rate')} placeholder="$0" />
        {rateErrors?.day_rate && <span className="error-text">{rateErrors.day_rate}</span>}
      </div>
      <div className={pair}>
        <span className="f-label">{isMobile ? 'Half-day · ex GST' : 'Half-day rate (ex GST)'}</span>
        <input className="f-input money" value={form.half_day_rate} onChange={set('half_day_rate')} placeholder="$0" />
        {rateErrors?.half_day_rate && <span className="error-text">{rateErrors.half_day_rate}</span>}
        <span className="f-help">
          {isMobile ? 'Blank = bookings suggest 50%' : 'Blank = no half-day rate (bookings suggest 50% of the day rate)'}
        </span>
      </div>
      <div className="f-field span-2">
        <span className="f-label">Notes</span>
        <textarea
          className="f-textarea"
          value={form.notes}
          onChange={set('notes')}
          placeholder="Travel area, deliverables, minimum call, gear…"
        />
      </div>
      {bookings && isMobile && (
        <div className="f-field span-2 crew-bookings-m">
          <div className="sec-l">Bookings</div>
          {!bookingsLoaded ? (
            <div className="empty-note">Loading bookings…</div>
          ) : bookings.length === 0 ? (
            <div className="empty-note">No project bookings.</div>
          ) : (
            bookings.map((booking) => (
              <Link
                className="crew-row"
                to={`/projects/${booking.project_id}`}
                key={`${booking.project_id}-${booking.start_date}`}
              >
                <span className={`phase-dot ${PHASE_DOTS[booking.phase]}`} />
                <span className="crew-col">
                  <span className="crew-row-title">
                    {booking.project_number !== null && `${formatProjectNumber(booking.project_number)} `}
                    {booking.project_name}
                  </span>
                  <span className="crew-row-meta">{crewBookingMeta(booking)}</span>
                </span>
                <span className="chev">›</span>
              </Link>
            ))
          )}
        </div>
      )}
      {bookings && !isMobile && (
        <div className="f-field span-2">
          <span className="f-label">Bookings</span>
          {!bookingsLoaded ? (
            <div className="empty-note">Loading bookings…</div>
          ) : bookings.length === 0 ? (
            <div className="empty-note">No project bookings.</div>
          ) : (
            <div className="crew-bookings">
              {bookings.map((booking) => {
                return (
                  <Link className="crew-booking" to={`/projects/${booking.project_id}`} key={`${booking.project_id}-${booking.start_date}`}>
                    <span className={`phase-dot ${PHASE_DOTS[booking.phase]}`} />
                    <span className="crew-booking-project" title={booking.project_name}>
                      {booking.project_number !== null && `${formatProjectNumber(booking.project_number)} `}
                      {booking.project_name}
                    </span>
                    <span className="crew-booking-dates">{crewBookingMeta(booking)}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
      {error && <p className="error-text span-2">{error}</p>}
    </div>
  );
}

function NewCrewModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState<CrewForm>(() => crewForm());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateErrors, setRateErrors] = useState<CrewRateErrors>({});

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!form.name.trim() || busy) return;
    const { dayRate, halfDayRate, errors: nextRateErrors } = parseCrewRates(form);
    if (Object.keys(nextRateErrors).length > 0) {
      setRateErrors(nextRateErrors);
      setError(null);
      return;
    }
    setBusy(true);
    setError(null);
    setRateErrors({});
    try {
      await api('/api/admin/crew', {
        method: 'POST',
        json: {
          name: form.name.trim(),
          role: form.role || null,
          company: form.company || null,
          email: form.email || null,
          phone: form.phone || null,
          day_rate_cents: dayRate,
          half_day_rate_cents: halfDayRate,
          notes: form.notes || null,
        },
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create crew contact.');
      setBusy(false);
    }
  };

  return (
    <Modal
      mobile="full"
      // "Create crew contact →" already fills the sheet header; the short
      // title is what the FAB and the page menu call it.
      title={isMobile ? 'New crew' : 'New crew contact'}
      context="CREW"
      width={520}
      onClose={onClose}
      hint="Everything but the name can be filled in later"
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => submit()} disabled={!form.name.trim() || busy}>
            Create crew contact →
          </button>
        </>
      }
    >
      <form onSubmit={submit}>
        <CrewFormFields
          form={form}
          setForm={setForm}
          autoFocus
          error={error}
          rateErrors={rateErrors}
          onRateEdit={(field) => {
            setRateErrors((current) => {
              if (!current[field]) return current;
              const next = { ...current };
              delete next[field];
              return next;
            });
          }}
        />
      </form>
    </Modal>
  );
}

function EditCrewModal({
  crew,
  onClose,
  onChanged,
}: {
  crew: CrewContactWithBookings;
  onClose: () => void;
  onChanged: () => void;
}) {
  const isMobile = useIsMobile();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState<CrewForm>(() => crewForm(crew));
  const [bookings, setBookings] = useState<CrewBooking[]>([]);
  const [bookingsLoaded, setBookingsLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateErrors, setRateErrors] = useState<CrewRateErrors>({});

  useEffect(() => {
    api<{ bookings: CrewBooking[] }>(`/api/admin/crew/${crew.id}/bookings`)
      .then((response) => setBookings(response.bookings))
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load bookings.'))
      .finally(() => setBookingsLoaded(true));
  }, [crew.id]);

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!form.name.trim() || busy) return;
    const { dayRate, halfDayRate, errors: nextRateErrors } = parseCrewRates(form);
    if (Object.keys(nextRateErrors).length > 0) {
      setRateErrors(nextRateErrors);
      setError(null);
      return;
    }
    setBusy(true);
    setError(null);
    setRateErrors({});
    try {
      await api(`/api/admin/crew/${crew.id}`, {
        method: 'PATCH',
        json: {
          name: form.name.trim(),
          role: form.role || null,
          company: form.company || null,
          phone: form.phone || null,
          email: form.email || null,
          day_rate_cents: dayRate,
          half_day_rate_cents: halfDayRate,
          notes: form.notes || null,
        },
      });
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update crew contact.');
      setBusy(false);
    }
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/admin/crew/${crew.id}`, { method: 'DELETE' });
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete crew contact.');
      setBusy(false);
    }
  };

  return (
    <Modal
      mobile="full"
      title="Edit crew member"
      // `.sheet-ctx` uppercases, so the mockup's "Crew · DOP" and the
      // desktop's "CREW · DOP" are the same string — keep the desktop one.
      context={crew.role ? `CREW · ${crew.role}` : 'CREW'}
      width={560}
      onClose={onClose}
      foot={
        isMobile ? (
          <>
            <button className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button className="btn primary" onClick={() => submit()} disabled={!form.name.trim() || busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        ) : (
          <>
            <span className="crew-modal-delete">
              <TrashAction label="Delete crew member" onConfirm={remove} disabled={busy} />
            </span>
            <button className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button className="btn primary" onClick={() => submit()} disabled={!form.name.trim() || busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        )
      }
    >
      <form onSubmit={submit}>
        <CrewFormFields
          form={form}
          setForm={setForm}
          bookings={bookings}
          bookingsLoaded={bookingsLoaded}
          error={error}
          rateErrors={rateErrors}
          onRateEdit={(field) => {
            setRateErrors((current) => {
              if (!current[field]) return current;
              const next = { ...current };
              delete next[field];
              return next;
            });
          }}
        />
      </form>
      {isMobile && (
        <div className="crew-delete-row">
          <SheetRow
            icon={<TrashIcon />}
            label="Delete crew member"
            tone="danger"
            disabled={busy}
            onSelect={() => setConfirmDelete(true)}
          />
        </div>
      )}
      {confirmDelete && (
        <Modal
          mobile="half"
          title="Delete crew member?"
          context={crew.name}
          onClose={() => {
            if (!busy) setConfirmDelete(false);
          }}
          foot={
            <>
              <button className="btn" disabled={busy} onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
              <button className="btn primary danger" disabled={busy} onClick={() => void remove()}>
                {busy ? 'Deleting…' : 'Delete'}
              </button>
            </>
          }
        >
          <p className="crew-confirm">
            {crew.name} goes to Trash for 30 days. Bookings already on projects are not changed.
          </p>
        </Modal>
      )}
    </Modal>
  );
}
